/**
 * PolicyPlanPreviewCard — #26 (2026-06-10).
 *
 * Approver-facing preview of a plan-carrying insurance_policies UPDATE CR
 * (`_detach_plan`, `_terminate_plan`, or `_move_plan`). The footprint was
 * snapshotted at FILING time; apply re-validates it and 409s on drift, so
 * what this card shows is exactly what approval will execute. Sibling of
 * CascadePlanPreviewCard (#262 agreements cascade). Also closes a gap:
 * move CRs (#104) previously rendered no plan context at all (underscore
 * keys are hidden from the payload diff table).
 */
import { Badge, Card, Group, Stack, Table, Text } from '@mantine/core';

import type { CloseReason } from '../api/types';
import { CLOSE_REASON_LABELS } from '../api/types';

import { AccountRef } from './EntityRef';

interface PlanAccountItem {
  id: string;
  label?: string;
}

export interface PolicyPlan {
  policy_label?: string;
  policy_current_status?: string;
  close_reason?: string;
  // detach/terminate shape:
  indirect_accounts?: PlanAccountItem[];
  // move shape (ids only, labels not stored):
  indirect_account_ids?: string[];
  target_agreement_id?: string;
}

interface Props {
  plan: PolicyPlan;
  flavour: 'detach' | 'terminate' | 'move';
}

const FLAVOUR_META = {
  detach: {
    title: 'Detach plan (filed with this request)',
    policyOutcome: 'Policy → Inactive limbo (detached from its Agreement)',
    accountOutcome: 'Inactive',
    colour: 'yellow',
  },
  terminate: {
    title: 'Termination plan (filed with this request)',
    policyOutcome: 'Policy → Closed (terminal — cannot be reversed)',
    accountOutcome: 'Closed (terminal)',
    colour: 'red',
  },
  move: {
    title: 'Move plan (filed with this request)',
    policyOutcome: 'Policy → Active on the target Agreement',
    accountOutcome: 'Inactive',
    colour: 'blue',
  },
} as const;

export function PolicyPlanPreviewCard({ plan, flavour }: Props) {
  const meta = FLAVOUR_META[flavour];
  // Normalise the two shapes: detach/terminate store {id,label} items;
  // move stores bare id strings (labels resolved live via AccountRef).
  const items: PlanAccountItem[] =
    plan.indirect_accounts
    ?? (plan.indirect_account_ids ?? []).map((id) => ({ id }));

  return (
    <Card withBorder p="md" radius="sm">
      <Stack gap="xs">
        <Group gap="xs" wrap="nowrap">
          <Text fw={600} size="sm">{meta.title}</Text>
          <Badge color={meta.colour} variant="light" size="xs">
            {flavour === 'terminate' && plan.close_reason
              ? `Terminate · ${CLOSE_REASON_LABELS[plan.close_reason as CloseReason] ?? plan.close_reason}`
              : flavour.charAt(0).toUpperCase() + flavour.slice(1)}
          </Badge>
        </Group>
        <Text size="sm">{meta.policyOutcome}</Text>
        {items.length === 0 ? (
          <Text size="sm" c="dimmed">
            No Active Indirect Accounts cascade with this operation.
          </Text>
        ) : (
          <>
            <Text size="sm">
              {items.length === 1
                ? `1 Indirect Account cascades to ${meta.accountOutcome}:`
                : `${items.length} Indirect Accounts cascade to ${meta.accountOutcome}:`}
            </Text>
            <Table withTableBorder>
              <Table.Tbody>
                {items.map((it) => (
                  <Table.Tr key={it.id}>
                    <Table.Td>
                      {it.label
                        ? <Text size="sm">{it.label}</Text>
                        : <AccountRef id={it.id} linkPathPrefix="/accounts" />}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </>
        )}
        <Text size="xs" c="dimmed">
          Snapshotted when the request was filed. If the Policy's accounts
          have changed since, approval is rejected with a conflict and the
          maker must re-file against current state.
        </Text>
      </Stack>
    </Card>
  );
}
